/** Ввод кода из SMS: ячейки с автопереходом, вставкой и автозаполнением. */

import {
  useEffect,
  useRef,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react';

interface CodeInputProps {
  length: number;
  value: string;
  disabled?: boolean;
  invalid?: boolean;
  onChange(next: string): void;
  /** Код набран целиком — можно отправлять, не дожидаясь нажатия кнопки. */
  onComplete(code: string): void;
}

export function CodeInput({
  length,
  value,
  disabled = false,
  invalid = false,
  onChange,
  onComplete,
}: CodeInputProps): JSX.Element {
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const completed = useRef(false);

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (value.length === length && !completed.current) {
      completed.current = true;
      onComplete(value);
    }
    if (value.length < length) completed.current = false;
  }, [value, length, onComplete]);

  const setDigit = (index: number, digit: string): void => {
    const chars = value.padEnd(length, ' ').split('');
    chars[index] = digit || ' ';
    const next = chars.join('').replace(/\s+$/, '').replace(/\s/g, '');
    onChange(next.slice(0, length));
  };

  const focusCell = (index: number): void => {
    const target = inputs.current[Math.min(length - 1, Math.max(0, index))];
    target?.focus();
    target?.select();
  };

  const handleInput = (index: number, raw: string): void => {
    const digits = raw.replace(/\D/g, '');
    if (digits.length === 0) {
      setDigit(index, '');
      return;
    }
    if (digits.length > 1) {
      // Автозаполнение из SMS приходит целой строкой в одну ячейку.
      const next = (value.slice(0, index) + digits).slice(0, length);
      onChange(next);
      focusCell(next.length);
      return;
    }
    const next = (value.slice(0, index) + digits + value.slice(index + 1)).slice(
      0,
      length,
    );
    onChange(next);
    focusCell(index + 1);
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      if (value[index]) {
        onChange(value.slice(0, index) + value.slice(index + 1));
        focusCell(index);
      } else {
        onChange(value.slice(0, Math.max(0, index - 1)));
        focusCell(index - 1);
      }
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusCell(index - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusCell(index + 1);
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>): void => {
    event.preventDefault();
    const digits = event.clipboardData.getData('text').replace(/\D/g, '');
    if (!digits) return;
    const next = digits.slice(0, length);
    onChange(next);
    focusCell(next.length);
  };

  return (
    <div
      className={`code${invalid ? ' code--invalid' : ''}`}
      role="group"
      aria-label={`Код из SMS, ${length} цифр`}
    >
      {Array.from({ length }, (_, index) => (
        <input
          key={index}
          ref={(node) => {
            inputs.current[index] = node;
          }}
          className="code__cell"
          type="text"
          inputMode="numeric"
          // Один и тот же autocomplete на всех ячейках — так iOS раскидывает код сам.
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={length}
          value={value[index] ?? ''}
          disabled={disabled}
          aria-label={`Цифра ${index + 1}`}
          aria-invalid={invalid}
          onChange={(event) => handleInput(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
          onFocus={(event) => event.target.select()}
        />
      ))}
    </div>
  );
}
